import React, { createContext, useContext, useState, useEffect } from 'react';
import { SettingsService } from '../services/api';

const SettingsContext = createContext();

export const useSettings = () => useContext(SettingsContext);

export const SettingsProvider = ({ children }) => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const [settings, setSettings] = useState({
        app_primary_color: '#7367F0',
        app_logo: null,
        currency: 'PKR',
        school_type: storedUser?.school?.type || storedUser?.data?.school?.type || 'academy'
    });
    const [loading, setLoading] = useState(true);

    const fetchSettings = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const response = await SettingsService.getSettings();
            const data = response.data;

            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            // Priority: fetched data > localStorage stored data > default 'academy'
            const schoolType = data.school_type || currentUser?.school?.type || currentUser?.data?.school?.type || 'academy';

            setSettings(prev => ({
                ...prev,
                ...data,
                app_logo: data.app_logo || null,
                currency: data.currency || 'PKR',
                school_type: schoolType
            }));

            // Sync with localStorage for components that rely on user object
            if (schoolType) {
                const user = JSON.parse(localStorage.getItem('user'));
                if (user) {
                    let updated = false;

                    // Update main school object
                    if (!user.school) {
                        user.school = { type: schoolType };
                        updated = true;
                    } else if (user.school.type !== schoolType) {
                        user.school.type = schoolType;
                        updated = true;
                    }

                    // Update nested data.school object (legacy compatibility)
                    if (user.data && user.data.school) {
                        if (user.data.school.type !== schoolType) {
                            user.data.school.type = schoolType;
                            updated = true;
                        }
                    }

                    if (updated) {
                        localStorage.setItem('user', JSON.stringify(user));
                        // No need to dispatch event here as we are updating the context itself which triggers re-renders
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching settings", error);
        } finally {
            setLoading(false);
        }
    };

    const hexToRgb = (hex) => {
        if (!hex) return null;
        // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
        var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
        hex = hex.replace(shorthandRegex, function (m, r, g, b) {
            return r + r + g + g + b + b;
        });

        var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
    };

    const applySettings = (currentSettings) => {
        if (currentSettings.app_primary_color) {
            const color = currentSettings.app_primary_color;
            const rgb = hexToRgb(color);

            document.documentElement.style.setProperty('--primary-color', color);

            // Update Bootstrap Variables dynamically
            document.documentElement.style.setProperty('--bs-primary', color);
            if (rgb) {
                document.documentElement.style.setProperty('--bs-primary-rgb', rgb);
            }
            document.documentElement.style.setProperty('--bs-link-color', color);
            document.documentElement.style.setProperty('--bs-btn-bg', color);
            document.documentElement.style.setProperty('--bs-btn-border-color', color);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    useEffect(() => {
        applySettings(settings);
    }, [settings]);

    const updateSettings = async (formData) => {
        const response = await SettingsService.updateSettings(formData);
        const data = response.data.settings || {};

        setSettings(prev => ({
            ...prev,
            ...data,
            app_logo: data.app_logo || null
        }));

        // Sync with localStorage user for terminology
        if (data.school_type) {
            const user = JSON.parse(localStorage.getItem('user'));
            if (user) {
                let updated = false;
                if (!user.school) {
                    user.school = { type: data.school_type };
                    updated = true;
                } else if (user.school.type !== data.school_type) {
                    user.school.type = data.school_type;
                    updated = true;
                }

                if (user.data && user.data.school && user.data.school.type !== data.school_type) {
                    user.data.school.type = data.school_type;
                    updated = true;
                }

                if (updated) {
                    localStorage.setItem('user', JSON.stringify(user));
                    window.dispatchEvent(new CustomEvent('profileUpdated', { detail: user }));
                }
            }
        }

        return response;
    };

    const getCurrencySymbol = (code) => {
        switch (code || settings.currency) {
            case 'PKR': return 'Rs. ';
            case 'EUR': return '€';
            case 'GBP': return '£';
            case 'INR': return '₹';
            case 'AED': return 'AED ';
            case 'USD': default: return '$';
        }
    };

    return (
        <SettingsContext.Provider value={{ settings, updateSettings, fetchSettings, loading, getCurrencySymbol }}>
            {children}
        </SettingsContext.Provider>
    );
};
